import React, { useState, useEffect, useRef } from 'react';
import emailjs from '@emailjs/browser';
import Testing from './Testing';
import Projects from './Projects';
import Services from './Services';
import './Home.css';

const Home = () => {
  const [activeTab, setActiveTab] = useState('history');
  const [activeTimelineTab, setActiveTimelineTab] = useState(0);
  const [currentPage, setCurrentPage] = useState('home');

  const [showScrollTop, setShowScrollTop] = useState(false);
  const [contactModalOpen, setContactModalOpen] = useState(false);
  const [showTopNavbar, setShowTopNavbar] = useState(false);
  const [lastScrollY, setLastScrollY] = useState(0);
  const [activeSection, setActiveSection] = useState('home');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitMessage, setSubmitMessage] = useState('');
  const [formErrors, setFormErrors] = useState({});
  const [isLoading, setIsLoading] = useState(false);
  
  // Carousel state
  const [carouselPosition, setCarouselPosition] = useState(0);

  const [formData, setFormData] = useState({
    name: '',
    email: '',
    mobile: '',
    message: ''
  });
  

  const scrollProgressRef = useRef(null);
  const videoRef = useRef(null);

  // Client logos data - all logos from ValuedClients folder with transparent backgrounds
     const clientLogos = [
     "ValuedClients/SS-removebg-preview.png",
     "ValuedClients/exan.png",
     "ValuedClients/firstsolid-removebg-preview.png",
     "ValuedClients/supermix-removebg-preview.png",
     "ValuedClients/teravera-removebg-preview.png",
     "ValuedClients/profriends-removebg-preview.png",
     "ValuedClients/upland-removebg-preview.png",
     "ValuedClients/tristar-removebg-preview.png",
     "ValuedClients/magnificon-removebg-preview.png",
     "ValuedClients/jaro-removebg-preview.png",
     "ValuedClients/infinity-removebg-preview.png",
     "ValuedClients/icon-removebg-preview.png",
     "ValuedClients/CMI-removebg-preview.png",
     "ValuedClients/citicon-removebg-preview.png",
     "ValuedClients/tokwing-removebg-preview.png",
     "ValuedClients/webcor-removebg-preview.png",
     "ValuedClients/mgs-removebg-preview.png",
     "ValuedClients/pap-removebg-preview.png",
     "ValuedClients/jfa-removebg-preview.png",
     "ValuedClients/granby-removebg-preview.png",
     "ValuedClients/bsp-removebg-preview.png",
     "ValuedClients/hyundai-removebg-preview.png",
     "ValuedClients/shimz-removebg-preview.png",
     "ValuedClients/ipm-removebg-preview.png",
     "ValuedClients/hpc-removebg-preview.png",
     "ValuedClients/ccd-removebg-preview.png",
     "ValuedClients/sta.rita-removebg-preview.png",
     "ValuedClients/smcc-removebg-preview.png",
     "ValuedClients/cct-removebg-preview.png",
     "ValuedClients/jcrcc-removebg-preview.png",
     "ValuedClients/borland-removebg-preview.png",
     "ValuedClients/BB-removebg-preview.png",
     "ValuedClients/stonerich-removebg-preview.png",
     "ValuedClients/Filinvest-removebg-preview.png",
     "ValuedClients/FirstBalfours-removebg-preview.png",
     "ValuedClients/MDC-removebg-preview.png",
     "ValuedClients/Megaworld-removebg-preview.png"
   ];

  // Scroll progress effect and navbar visibility
  useEffect(() => {
    const handleScroll = () => {
      const scrollTop = window.pageYOffset;
      const docHeight = document.body.offsetHeight - window.innerHeight;
      const scrollPercent = (scrollTop / docHeight) * 100;
      
      if (scrollProgressRef.current) {
        scrollProgressRef.current.style.strokeDashoffset = 125.6 - (125.6 * scrollPercent) / 100;
      }
      
      // Show/hide scroll to top button
      setShowScrollTop(scrollTop > 300);
      
      // Navbar visibility logic for home section
      if (scrollTop <= 100) {
        // At the top of the page (home section) - show navbar
        setShowTopNavbar(true);
      } else if (scrollTop < lastScrollY) {
        // Scrolling up - show navbar
        setShowTopNavbar(true);
      } else {
        // Scrolling down - hide navbar
        setShowTopNavbar(false);
      }
      
      // Scroll spy for active section highlighting
      if (currentPage === 'home') {
        const sections = ['home', 'services', 'projects', 'branches', 'testing'];
        let currentSection = 'home';
        let minDistance = Infinity;
        const viewportCenter = window.innerHeight / 2;
        
        sections.forEach(sectionId => {
          const element = document.getElementById(sectionId);
          if (element) {
            const rect = element.getBoundingClientRect();
            const sectionTop = rect.top;
            const sectionBottom = rect.bottom;
            
            // Calculate distance from viewport center to section center
            const sectionCenter = sectionTop + (rect.height / 2);
            const distance = Math.abs(viewportCenter - sectionCenter);
            
            // Check if section is in view and closest to viewport center
            if (sectionTop <= viewportCenter && sectionBottom >= viewportCenter && distance < minDistance) {
              minDistance = distance;
              currentSection = sectionId;
            }
            // If no section is centered, find the one closest to the top of viewport
            else if (sectionTop <= 300 && sectionTop >= -300 && distance < minDistance) {
              minDistance = distance;
              currentSection = sectionId;
            }
          }
        });
        
        // Special handling for testimonials section (Valued Clients)
        // If we're in the testimonials section, highlight projects
        const testimonialsElement = document.getElementById('testimonials');
        if (testimonialsElement) {
          const testimonialsRect = testimonialsElement.getBoundingClientRect();
          if (testimonialsRect.top <= viewportCenter && testimonialsRect.bottom >= viewportCenter) {
            currentSection = 'projects'; // Highlight projects when in testimonials
          }
        }
        
        // Special handling for footer area
        // If we're near the bottom of the page (footer area), highlight the last visible section
        const documentHeight = document.documentElement.scrollHeight;
        const windowHeight = window.innerHeight;
        const scrollTop = window.pageYOffset || document.documentElement.scrollTop;
        const scrollBottom = scrollTop + windowHeight;
        
        // If we're in the last 20% of the page (footer area), find the closest section
        if (scrollBottom >= documentHeight * 0.8) {
          let closestSection = 'testing'; // Default to testing (last main section)
          let closestDistance = Infinity;
          
          // Check which section is closest to the current scroll position
          sections.forEach(sectionId => {
            const element = document.getElementById(sectionId);
            if (element) {
              const rect = element.getBoundingClientRect();
              const sectionTop = rect.top + scrollTop;
              const distance = Math.abs(scrollTop - sectionTop);
              
              if (distance < closestDistance) {
                closestDistance = distance;
                closestSection = sectionId;
              }
            }
          });
          
          currentSection = closestSection;
        }
        
        setActiveSection(currentSection);
      }
      setLastScrollY(scrollTop);
    };

    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, [lastScrollY]);



  // Scroll-triggered animations for sections - Continuous animation on scroll
  useEffect(() => {
    const observerOptions = {
      threshold: [0, 0.1, 0.2, 0.3, 0.4, 0.5, 0.6, 0.7, 0.8, 0.9, 1.0],
      rootMargin: '0px 0px -50px 0px'
    };

    const observer = new IntersectionObserver((entries) => {
      entries.forEach((entry) => {
        if (entry.isIntersecting) {
          entry.target.classList.add('section-visible');
        } else {
          // Reset animation when element leaves viewport
          entry.target.classList.remove('section-visible');
        }
      });
    }, observerOptions);

    // Observe all sections with section-animate class
    const sections = document.querySelectorAll('.section-animate');
    sections.forEach((section) => {
      observer.observe(section);
    });

    return () => {
      sections.forEach((section) => {
        observer.unobserve(section);
      });
    };
  }, [currentPage]);

  // Scroll-triggered animations for branch cards (left/middle/right behaviors) - Continuous animation
  useEffect(() => {
    if (currentPage !== 'home') return;

    const cardObserver = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            entry.target.classList.add('in-view');
          } else {
            // Reset animation when element leaves viewport
            entry.target.classList.remove('in-view');
          }
        });
      },
      { threshold: [0, 0.1, 0.2, 0.3, 0.4, 0.5, 0.6, 0.7, 0.8, 0.9, 1.0], rootMargin: '0px 0px -40px 0px' }
    );

    const cards = document.querySelectorAll('.branches-section .branch-card');
    cards.forEach((el) => cardObserver.observe(el));

    return () => {
      cards.forEach((el) => cardObserver.unobserve(el));
    };
  }, [currentPage]);

  // Enhanced scroll-based animations for continuous effects
  useEffect(() => {
    const handleScrollAnimations = () => {
      const scrollTop = window.pageYOffset;
      const windowHeight = window.innerHeight;
      
      // Animate elements based on scroll position
      const animatedElements = document.querySelectorAll('.section-animate, .branch-card, .home-fadein');
      
      animatedElements.forEach((element) => {
        const rect = element.getBoundingClientRect();
        const elementTop = rect.top + scrollTop;
        const elementHeight = rect.height;
        
        // Calculate how much of the element is visible
        const elementCenter = elementTop + elementHeight / 2;
        const viewportCenter = scrollTop + windowHeight / 2;
        const distanceFromCenter = Math.abs(elementCenter - viewportCenter);
        const maxDistance = windowHeight;
        
        // Calculate animation progress (0 to 1)
        const animationProgress = Math.max(0, 1 - (distanceFromCenter / maxDistance));
        
        // Apply smooth animation based on scroll position
        if (animationProgress > 0.1) {
          element.classList.add('section-visible');
          // Add dynamic opacity and transform based on scroll position
          const opacity = Math.min(1, animationProgress * 1.5);
          const translateY = (1 - animationProgress) * 30;
          const scale = 0.95 + (animationProgress * 0.05);
          
          element.style.setProperty('--scroll-opacity', opacity);
          element.style.setProperty('--scroll-translate-y', `${translateY}px`);
          element.style.setProperty('--scroll-scale', scale);
          
          // Special handling for branch cards with directional animations
          if (element.classList.contains('branch-card')) {
            const initialTranslateX = element.classList.contains('branch-pos-left') ? -40 : 
                                    element.classList.contains('branch-pos-right') ? 40 : 0;
            const translateX = initialTranslateX * (1 - animationProgress);
            element.style.setProperty('--scroll-translate-x', `${translateX}px`);
          }
        } else {
          element.classList.remove('section-visible');
        }
      });
    };

    // Throttle scroll events for better performance
    let ticking = false;
    const throttledScrollHandler = () => {
      if (!ticking) {
        requestAnimationFrame(() => {
          handleScrollAnimations();
          ticking = false;
        });
        ticking = true;
      }
    };

    window.addEventListener('scroll', throttledScrollHandler, { passive: true });
    
    // Initial call
    handleScrollAnimations();

    return () => {
      window.removeEventListener('scroll', throttledScrollHandler);
    };
  }, [currentPage]);

  // Video fade-in effect and auto-play handling
  useEffect(() => {
    if (videoRef.current && currentPage === 'home') {
      // Add a small delay to ensure video is loaded
      const timer = setTimeout(() => {
        videoRef.current.classList.add('visible');
        
        // Reset video to beginning
        videoRef.current.currentTime = 0;
        
        // Try to play video with proper error handling for Brave browser
        const playVideo = async () => {
          try {
            // For Brave browser, try multiple approaches
            if (videoRef.current.paused) {
              // First attempt - direct play
              await videoRef.current.play();
            }
          } catch (e) {
            // Only log non-abort errors to reduce console spam
            if (e.name !== 'AbortError') {
              console.log('Video play error:', e);
            }
            
            // Try alternative approach for Brave browser
            try {
              // Set video properties for better Brave compatibility
              videoRef.current.muted = true;
              videoRef.current.playsInline = true;
              videoRef.current.setAttribute('webkit-playsinline', 'true');
              videoRef.current.setAttribute('playsinline', 'true');
              
              // Try playing again
              await videoRef.current.play();
            } catch (e2) {
              console.log('Video retry failed:', e2);
              // Show fallback background if video fails to play
              if (videoRef.current) {
                videoRef.current.style.display = 'none';
                videoRef.current.parentElement.style.background = 'linear-gradient(135deg, #FFFFFF 0%, #808080 100%)';
              }
            }
          }
        };
        
        playVideo();
      }, 500);
      return () => clearTimeout(timer);
    }
  }, [currentPage]);

  // Handle video play/pause when page visibility changes
  useEffect(() => {
    const handleVisibilityChange = () => {
      if (videoRef.current) {
        if (document.hidden) {
          // Page is hidden, pause video
          videoRef.current.pause();
        } else {
          // Page is visible, resume video
          if (currentPage === 'home') {
            videoRef.current.play().catch(e => {
              // Only log non-abort errors to reduce console spam
              if (e.name !== 'AbortError') {
                console.log('Video play error:', e);
              }
              // If video fails to play, show fallback background
              if (videoRef.current) {
                videoRef.current.style.display = 'none';
                videoRef.current.parentElement.style.background = 'linear-gradient(135deg, #FFFFFF 0%, #808080 100%)';
              }
            });
          }
        }
      }
    };

    // Handle user interaction for Brave browser video play
    const handleUserInteraction = () => {
      if (videoRef.current && currentPage === 'home' && videoRef.current.paused) {
        videoRef.current.play().catch(e => {
          console.log('User interaction video play failed:', e);
        });
      }
    };

    const handlePageShow = (e) => {
      if (e.persisted && videoRef.current && currentPage === 'home') {
        // Page is restored from cache, resume video
        videoRef.current.play().catch(e => {
          if (e.name !== 'AbortError') {
            console.log('Video play error:', e);
          }
        });
      }
    };

    const handleFocus = () => {
      if (videoRef.current && currentPage === 'home') {
        // Window regained focus, resume video
        videoRef.current.play().catch(e => {
          if (e.name !== 'AbortError') {
            console.log('Video play error:', e);
          }
        });
      }
    };

    const handleBlur = () => {
      if (videoRef.current) {
        // Window lost focus, pause video
        videoRef.current.pause();
      }
    };

    // Add event listeners
    document.addEventListener('visibilitychange', handleVisibilityChange);
    window.addEventListener('pageshow', handlePageShow);
    window.addEventListener('focus', handleFocus);
    window.addEventListener('blur', handleBlur);
    
    // Add user interaction listeners for Brave browser
    document.addEventListener('click', handleUserInteraction);
    document.addEventListener('touchstart', handleUserInteraction);
    document.addEventListener('keydown', handleUserInteraction);

    // Cleanup
    return () => {
      document.removeEventListener('visibilitychange', handleVisibilityChange);
      window.removeEventListener('pageshow', handlePageShow);
      window.removeEventListener('focus', handleFocus);
      window.removeEventListener('blur', handleBlur);
      document.removeEventListener('click', handleUserInteraction);
      document.removeEventListener('touchstart', handleUserInteraction);
      document.removeEventListener('keydown', handleUserInteraction);
    };
  }, [currentPage]);

  // Auto-sliding carousel effect
  useEffect(() => {
    const interval = setInterval(() => {
      setCarouselPosition(prev => {
        // Move one card width to the left (slow sliding)
        const cardWidth = 230; // Width of each card (200px) + gap (30px)
        const newPosition = prev - 1; // Move 1px at a time for smooth sliding
        
        // Create infinite loop by resetting to the beginning of the duplicated set
        if (Math.abs(newPosition) >= cardWidth * clientLogos.length) {
          return 0;
        }
        return newPosition;
      });
    }, 50); // Update every 50ms for smooth movement

    return () => clearInterval(interval);
  }, [clientLogos.length]);

  // Handle initial page load and URL hash changes
  useEffect(() => {
    const hash = window.location.hash.replace('#', '');
    if (hash && hash !== 'home') {
      setCurrentPage(hash);
      setActiveSection(hash);
    } else {
      setCurrentPage('home');
      setActiveSection('home');
    }
  }, []);

  // Update URL hash when currentPage changes and handle video
  useEffect(() => {
    if (currentPage === 'home') {
      window.location.hash = '';
      setActiveSection('home');
      // Force video restart when switching to home
      if (videoRef.current) {
        // Reset video to beginning and play
        videoRef.current.currentTime = 0;
        videoRef.current.load(); // Reload the video
        setTimeout(() => {
          videoRef.current.play().catch(e => {
            console.log('Video play error when switching to home:', e);
            // Show fallback if video fails
            if (videoRef.current) {
              videoRef.current.style.display = 'none';
              videoRef.current.parentElement.style.background = 'linear-gradient(135deg, #1a1a1a 0%, #2a2a2a 100%)';
            }
          });
        }, 200);
      }
    } else {
      window.location.hash = currentPage;
      setActiveSection(currentPage);
      // Pause video when leaving home
      if (videoRef.current) {
        videoRef.current.pause();
      }
    }
  }, [currentPage]);







  // Scroll to top function
  const scrollToTop = () => {
    window.scrollTo({
      top: 0,
      behavior: 'smooth'
    });
  };

  // Open contact modal
  const openContactModal = () => {
    setContactModalOpen(true);
  };

  // Close contact modal
  const closeContactModal = () => {
    setContactModalOpen(false);
    setSubmitMessage('');
    setFormData({ name: '', email: '', mobile: '', message: '' });
  };

  // Handle input changes
  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
    
    // Clear error when user starts typing
    if (formErrors[name]) {
      setFormErrors(prev => ({
        ...prev,
        [name]: ''
      }));
    }
  };

  // Validate phone number format
  const validatePhoneNumber = (phone) => {
    // Remove all non-digit characters
    const cleanPhone = phone.replace(/\D/g, '');
    
    // Check if it's a valid Philippine mobile number
    // Should start with 09 and be 11 digits total
    if (cleanPhone.length !== 11) {
      return 'Phone number must be 11 digits';
    }
    
    if (!cleanPhone.startsWith('09')) {
      return 'Phone number must start with 09';
    }
    
    return '';
  };

  // Validate form before submission
  const validateForm = () => {
    const errors = {};
    
    // Name validation
    if (!formData.name.trim()) {
      errors.name = 'Name is required';
    } else if (formData.name.trim().length < 2) {
      errors.name = 'Name must be at least 2 characters';
    }
    
    // Email validation
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!formData.email.trim()) {
      errors.email = 'Email is required';
    } else if (!emailRegex.test(formData.email)) {
      errors.email = 'Please enter a valid email address';
    }
    
    // Phone validation
    if (!formData.mobile.trim()) {
      errors.mobile = 'Mobile number is required';
    } else {
      const phoneError = validatePhoneNumber(formData.mobile);
      if (phoneError) {
        errors.mobile = phoneError;
      }
    }
    
    // Message validation
    if (!formData.message.trim()) {
      errors.message = 'Message is required';
    } else if (formData.message.trim().length < 10) {
      errors.message = 'Message must be at least 10 characters';
    }
    
    setFormErrors(errors);
    return Object.keys(errors).length === 0;
  };

  // Handle click outside modal to close
  const handleModalBackdropClick = (e) => {
    if (e.target.id === 'contact-modal') {
      closeContactModal();
    }
  };

  // Handle contact form submission
  const handleContactSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    setSubmitMessage('');

    if (!validateForm()) {
      setIsSubmitting(false);
      return;
    }

    try {
      // EmailJS configuration - YOU NEED TO SET THESE UP
      const serviceId = 'service_q4e4t5m'; // Replace with your service ID
      const templateId = 'template_thvv9x8'; // Replace with your template ID
      const publicKey = 'G6HIIYC6vGUII5Lio'; // Replace with your public key

      // Initialize EmailJS if not already done
      if (!emailjs.init) {
        emailjs.init(publicKey);
      }

      // Send email using EmailJS
      const result = await emailjs.send(
        serviceId,
        templateId,
        {
          from_name: formData.name,
          from_email: formData.email,
          from_mobile: formData.mobile,
          message: formData.message,
          to_email: 'geomatixs.information@gmail.com'
        },
        publicKey
      );

      console.log('EmailJS result:', result);
      
      if (result.status === 200) {
        setSubmitMessage('Message sent successfully! We\'ll get back to you soon.');
        // Clear form after successful submission
        setFormData({ name: '', email: '', mobile: '', message: '' });
        // Close modal after 3 seconds
        setTimeout(() => {
          closeContactModal();
        }, 3000);
      } else {
        throw new Error(`EmailJS returned status ${result.status}`);
      }
    } catch (error) {
      console.error('Email sending failed:', error);
      setSubmitMessage(`Failed to send message: ${error.message}. Please try again or contact us directly at geomatixs.information@gmail.com`);
    } finally {
      setIsSubmitting(false);
    }
  };

  // Timeline data
  const timelineData = {
    0: [
      { date: 'July 2020', description: 'Geomatixs founded its first laboratory in Carmona, Cavite, and Sto. Tomas, Batangas. Armed with its core principles—service, value, and excellence—Geomatixs proceeded to serve its clients with a guarantee of satisfaction.' },
      { date: 'April 2021', description: 'Geomatixs introduced to the construction industry the country\'s newest technology of electric density gauge, which rapidly accomplished field density tests with great accuracy.' },
      { date: 'September 2021', description: 'Geomatixs opened its third laboratory in General Trias, Cavite to further expand its service coverage and cater to more of the clients\' testing requirements.' }
    ],
    1: [
      { date: 'March 2022', description: 'Geomatixs opened its fourth laboratory to serve the growing industry in the district of Silang-Tagaytay, Cavite.' },
      { date: 'March 2023', description: 'Geomatixs opened its fifth laboratory in Tanza, Cavite to further serve clients in Kawit, Naic, and Tanza.' }
    ],
    2: [
      { date: 'February 2024', description: 'Geomatixs opened its sixth laboratory in Limatech Lipa, Batangas to cater to more clients in the Batangas area.' },
      { date: 'March 2025', description: 'Geomatixs opened its in-house laboratory at Banlic, Calamba Laguna for the NSCR CP-507 project (LOTTE GULERMAK EEI JOINT VENTURE).' }
    ]
  };

  // Branches data
  const branches = [
    {
      name: 'Carmona Branch',
      location: 'Unit 24 Paseo de Carmona Bldg. Brgy, Carmona, Cavite, Philippines',
      contact: '+63 46 123 4567',
      city: 'Carmona, Cavite',
      address: 'Unit 24 Paseo de Carmona Bldg. Brgy',
      isMain: true,
      mapLink: 'https://maps.google.com/?q=Geomatixs+Engineering+Carmona+Cavite'
    },
    {
      name: 'Tanza Branch',
      location: '9VQ3+CFV, Tanza - Trece Martires Rd, Tanza, Cavite',
      contact: '+63 46 234 5678',
      city: 'Tanza, Cavite',
      address: 'Tanza - Trece Martires Rd',
      isMain: false,
      mapLink: 'https://maps.google.com/?q=Geomatixs+Engineering+Tanza+Cavite'
    },
    {
      name: 'Gentri Branch',
      location: 'MMSB Commercial Bldg, Brgy. Santiago Arnaldo Hwy, General Trias, Cavite',
      contact: '+63 46 345 6789',
      city: 'General Trias, Cavite',
      address: 'MMSB Commercial Bldg, Brgy. Santiago Arnaldo Hwy',
      isMain: false,
      mapLink: 'https://maps.google.com/?q=Geomatixs+Engineering+General+Trias+Cavite'
    },
    {
      name: 'Batangas Branch',
      location: '448R+GHM, 1, Santo Tomas, Batangas',
      contact: '+63 43 456 7890',
      city: 'Santo Tomas, Batangas',
      address: '448R+GHM, 1',
      isMain: false,
      mapLink: 'https://maps.google.com/?q=Geomatixs+Engineering+Batangas+City'
    },
    {
      name: 'Silang Branch',
      location: 'Aguinaldo Hwy, Lalaan 2, Silang, Cavite',
      contact: '+63 46 567 8901',
      city: 'Silang, Cavite',
      address: 'Aguinaldo Hwy, Lalaan 2',
      isMain: false,
      mapLink: 'https://maps.google.com/?q=Geomatixs+Engineering+Silang+Cavite'
    },
    {
      name: 'Lima Tech Branch',
      location: 'Purok 3, 199-B Jose Laurel Hwy, Lipa City, 4217 Batangas',
      contact: '+63 43 678 9012',
      city: 'Lima Tech, Batangas',
      address: 'Lima Technology Center',
      isMain: false,
      mapLink: 'https://maps.google.com/?q=Geomatixs+Engineering+Lima+Technology+Center+Batangas'
    },
    {
      name: 'Banlic Branch',
      location: '65G3+9H5, Calamba, 4027 Laguna',
      contact: '+63 46 789 0123',
      city: 'Banlic, Laguna',
      address: '65G3+9H5, Calamba',
      isMain: false,
      mapLink: 'https://maps.google.com/?q=Geomatixs+Engineering+Banlic+Laguna'
    },
    {
      name: 'Balayan Branch',
      location: 'Unit 1, Superin Bldg, Brgy Gumamela, Lemery Road, Balayan, 4213 Batangas',
      contact: '+63 43 789 0123',
      city: 'Balayan, Batangas',
      address: 'Unit 1, Superin Bldg, Brgy Gumamela, Lemery Road',
      isMain: false,
      mapLink: 'https://maps.google.com/?q=Geomatixs+Engineering+Balayan+Batangas'
    }
  ];



  return (
    <div className="home">
      {/* Loading Screen */}
      {isLoading && (
        <div className="loading-screen">
          <div className="loading-content">
            <img 
              src={`${process.env.PUBLIC_URL}/geologo-new.png`} 
              alt="Geomatixs Group Corporation" 
              className="loading-logo"
              onLoad={() => console.log('Image loaded successfully')}
              onError={(e) => {
                console.log('Image failed to load:', e.target.src);
                console.log('Trying fallback...');
                e.target.src = '/geologo.png';
              }}
            />
            <p className="loading-text">Loading Geomatixs...</p>
          </div>
        </div>
      )}

      {/* New Icon Navbar */}
      <div className={`icon-navbar ${showTopNavbar ? 'visible' : 'hidden'}`}>
        {/* Desktop Navigation */}
        <div className="button-container">
          {/* GGC Logo */}
          <div className="nav-logo">
            <img 
              src={`${process.env.PUBLIC_URL}/geologo.png`} 
              alt="Geomatixs Group Corporation" 
              className="nav-logo-img"
            />
          </div>
          
          <button 
            className={`button ${currentPage === 'home' && activeSection === 'home' ? 'active' : ''}`}
            onClick={() => {
              // Always go to home page, regardless of current page
              setCurrentPage('home');
              setActiveSection('home');
              // Only reload if we're already on home page
              if (currentPage === 'home') {
                window.location.reload();
              }
            }}
            title="Home"
          >
            Home
          </button>
          
          <button 
            className={`button ${currentPage === 'services' || activeSection === 'services' ? 'active' : ''}`}
            onClick={() => {
              setCurrentPage('services');
              setActiveSection('services');
              window.scrollTo(0, 0);
            }}
            title="Services"
          >
            Services
          </button>

          <button 
            className={`button ${currentPage === 'projects' || activeSection === 'projects' ? 'active' : ''}`}
            onClick={() => {
              setCurrentPage('projects');
              setActiveSection('projects');
              window.scrollTo(0, 0);
            }}
            title="Projects"
          >
            Projects
          </button>

          <button 
            className={`button ${activeSection === 'branches' ? 'active' : ''}`}
            onClick={(e) => {
              e.preventDefault();
              // If we're not on home page, show loading screen and go to home first, then scroll to branches
              if (currentPage !== 'home') {
                setIsLoading(true);
                setCurrentPage('home');
                // Wait a bit for the page to change, then scroll to branches
                setTimeout(() => {
                  setActiveSection('branches');
                  const branchesSection = document.getElementById('branches');
                  if (branchesSection) {
                    branchesSection.scrollIntoView({ behavior: 'smooth' });
                  }
                  // Hide loading screen after navigation is complete
                  setTimeout(() => {
                    setIsLoading(false);
                  }, 500);
                }, 100);
              } else {
                // If already on home page, just scroll to branches
                setActiveSection('branches');
                const branchesSection = document.getElementById('branches');
                if (branchesSection) {
                  branchesSection.scrollIntoView({ behavior: 'smooth' });
                }
              }
            }}
            title="Branches"
          >
            Branches
          </button>

          <button 
            className={`button ${currentPage === 'testing' || activeSection === 'testing' ? 'active' : ''}`}
            onClick={() => {
              setCurrentPage('testing');
              setActiveSection('testing');
              window.scrollTo(0, 0);
            }}
            title="Testing"
          >
            Testing
          </button>


        </div>

        {/* Mobile Top Navigation */}
        <div className="mobile-nav-container">
          <div className="mobile-logo">GEOMATIXS</div>
          <button 
            className="mobile-menu-toggle"
            onClick={() => {
              const menu = document.querySelector('.mobile-menu');
              if (menu) {
                menu.classList.toggle('active');
              }
            }}
          >
            <svg viewBox="0 0 24 24" fill="currentColor">
              <path d="M3 18h18v-2H3v2zm0-5h18v-2H3v2zm0-7v2h18V6H3z"/>
            </svg>
          </button>
        </div>

        {/* Mobile Dropdown Menu */}
        <div className="mobile-menu">
          <button 
            className={`mobile-menu-item ${currentPage === 'home' || activeSection === 'home' ? 'active' : ''}`}
            onClick={() => {
              setCurrentPage('home');
              setActiveSection('home');
              window.scrollTo({ top: 0, behavior: 'smooth' });
              document.querySelector('.mobile-menu')?.classList.remove('active');
            }}
          >
            <svg viewBox="0 0 24 24" fill="currentColor">
              <path d="M10 20v-6h4v6h5v-8h3L12 3 2 12h3v8z"/>
            </svg>
            <span>Home</span>
          </button>
          <button 
            className={`mobile-menu-item ${currentPage === 'services' || activeSection === 'services' ? 'active' : ''}`}
            onClick={() => {
              setCurrentPage('services');
              setActiveSection('services');
              window.scrollTo(0, 0);
              document.querySelector('.mobile-menu')?.classList.remove('active');
            }}
          >
            <svg viewBox="0 0 24 24" fill="currentColor">
              <path d="M12 2l3.09 6.26L22 9.27l-5 4.87 1.18 6.88L12 17.77l-6.18 3.25L7 14.14 2 9.27l6.91-1.01L12 2z"/>
            </svg>
            <span>Services</span>
          </button>
          <button 
            className={`mobile-menu-item ${currentPage === 'projects' || activeSection === 'projects' ? 'active' : ''}`}
            onClick={() => {
              setCurrentPage('projects');
              setActiveSection('projects');
              window.scrollTo(0, 0);
              document.querySelector('.mobile-menu')?.classList.remove('active');
            }}
          >
            <svg viewBox="0 0 24 24" fill="currentColor">
              <path d="M19 3H5c-1.1 0-2 .9-2 2v14c0 1.1.9 2 2 2h14c1.1 0 2-.9 2-2V5c0-1.1-.9-2-2-2zm-5 14H7v-2h7v2zm3-4H7v-2h10v2zm0-4H7V7h10v2z"/>
            </svg>
            <span>Projects</span>
          </button>
          <button 
            className={`mobile-menu-item ${activeSection === 'branches' ? 'active' : ''}`}
            onClick={(e) => {
              e.preventDefault();
              if (currentPage !== 'home') {
                setCurrentPage('home');
                setTimeout(() => {
                  setActiveSection('branches');
                  const branchesSection = document.getElementById('branches');
                  if (branchesSection) {
                    branchesSection.scrollIntoView({ behavior: 'smooth' });
                  }
                }, 100);
              } else {
                setActiveSection('branches');
                const branchesSection = document.getElementById('branches');
                if (branchesSection) {
                  branchesSection.scrollIntoView({ behavior: 'smooth' });
                }
              }
              document.querySelector('.mobile-menu')?.classList.remove('active');
            }}
          >
            <svg viewBox="0 0 24 24" fill="currentColor">
              <path d="M12 2C8.13 2 5 5.13 5 9c0 5.25 7 13 7 13s7-7.75 7-13c0-3.87-3.13-7-7-7zm0 9.5c-1.38 0-2.5-1.12-2.5-2.5s1.12-2.5 2.5-2.5 2.5 1.12 2.5 2.5-1.12 2.5-2.5 2.5z"/>
            </svg>
            <span>Branches</span>
          </button>
          <button 
            className={`mobile-menu-item ${currentPage === 'testing' || activeSection === 'testing' ? 'active' : ''}`}
            onClick={() => {
              setCurrentPage('testing');
              setActiveSection('testing');
              window.scrollTo(0, 0);
              document.querySelector('.mobile-menu')?.classList.remove('active');
            }}
          >
            <svg viewBox="0 0 24 24" fill="currentColor">
              <path d="M9 11H7v6h2v-6zm4 0h-2v6h2v-6zm4 0h-2v6h2v-6zm2.5-9H19V1h-2v1H7V1H5v1H4.5C3.67 2 3 2.67 3 3.5v15C3 19.33 3.67 20 4.5 20h15c.83 0 1.5-.67 1.5-1.5v-15C21 2.67 20.33 2 19.5 2z"/>
            </svg>
            <span>Testing</span>
          </button>
        </div>
      </div>



                          {/* Page Navigation */}

       {/* Conditional Page Rendering */}
       {currentPage === 'testing' && (
         <div style={{ width: '100%', minHeight: '100vh', background: '#1a1a1a', paddingTop: '80px' }}>
           <Testing setCurrentPage={setCurrentPage} setActiveSection={setActiveSection} />
         </div>
       )}


       {currentPage === 'projects' && <Projects setCurrentPage={setCurrentPage} setActiveSection={setActiveSection} />}
       {currentPage === 'services' && <Services setCurrentPage={setCurrentPage} setActiveSection={setActiveSection} />}

              {/* Hero Section */}
       {currentPage === 'home' && (
         <section id="home" className="hero">
           <div className="hero-background">
                           <video 
                ref={videoRef}
                id="home-bg-video" 
                autoPlay 
                loop 
                muted 
                playsInline 
                preload="auto"
                poster={`${process.env.PUBLIC_URL}/bgphoto.png`}
                webkit-playsinline="true"
                x5-video-player-type="h5"
                x5-video-player-fullscreen="true"
                crossOrigin="anonymous"
                style={{
                  width: '100%',
                  height: '100%',
                  objectFit: 'cover',
                  position: 'absolute',
                  top: 0,
                  left: 0,
                  right: 0,
                  bottom: 0
                }}
                className="video-fade"
                onLoadStart={() => {
                  console.log('Video loading started');
                }}
                onCanPlay={() => {
                  console.log('Video can play');
                  // Try to play immediately when video is ready
                  if (videoRef.current && videoRef.current.paused) {
                    videoRef.current.play().catch(e => {
                      console.log('Auto-play failed, will retry:', e);
                    });
                  }
                }}
                onLoadedData={() => {
                  console.log('Video data loaded');
                  // Another attempt to play when data is loaded
                  if (videoRef.current && videoRef.current.paused) {
                    videoRef.current.play().catch(e => {
                      console.log('Auto-play on data load failed:', e);
                    });
                  }
                }}
                onError={(e) => {
                  console.log('Hero video error:', e);
                  e.target.style.display = 'none';
                  // Add a fallback background
                  e.target.parentElement.style.background = 'linear-gradient(135deg, #FFFFFF 0%, #808080 100%)';
                }}
              >
                {/* Multiple video sources for better mobile compatibility */}
                <source src={`${process.env.PUBLIC_URL}/bgvideo.mp4`} type="video/mp4" />
                <source src={`${process.env.PUBLIC_URL}/bgvideo.webm`} type="video/webm" />
                <source src={`${process.env.PUBLIC_URL}/bgvideo.ogv`} type="video/ogg" />
                {/* Fallback message for browsers that don't support video */}
                Your browser does not support the video tag.
              </video>
             <div className="hero-overlay"></div>
           </div>
           <div className="hero-content">
             <div className="hero-text">
               <h1 className="hero-title home-title-shadow home-fadein home-fadein-1">
                 <span className="geo-text">GEO</span>
                 <span className="matixs-text">MATIXS</span>
                 <span className="group-text"> GROUP CORP.</span>
               </h1>
               <p className="hero-subtitle home-fadein home-fadein-2">Engineering Laboratory for Industrial & Commercial Industry</p>
               <div className="hero-buttons home-fadein home-fadein-3">
                 <a href="#contact" className="hero-btn primary" onClick={openContactModal}>Contact Us</a>
                 <a
                   href="https://www.facebook.com/geomatixs"
                   target="_blank"
                   rel="noopener noreferrer"
                   className="hero-btn social facebook-btn"
                   aria-label="Follow us on Facebook"
                 >
                   <span style={{ fontSize: '20px', fontWeight: 'bold', color: 'white' }}>f</span>
                 </a>
               </div>
             </div>
             <div className="hero-logo">
               <img 
                 src={`${process.env.PUBLIC_URL}/geologo.png`} 
                 alt="Geomatixs Group Logo" 
                 className="hero-logo-img home-logo-effect"
                 style={{
                   width: '260px',
                   height: '260px',
                   objectFit: 'contain',
                   borderRadius: '2rem',
                   border: '4px solid #000000',
                   background: '#fff',
                   boxShadow: '0 8px 40px 0 rgba(255,211,0,0.18), 0 2px 16px 0 rgba(0,0,0,0.18)'
               }}
                 onError={(e) => {
                   console.log('Hero logo error:', e);
                   e.target.style.display = 'none';
                   // Add fallback logo
                   const fallback = document.createElement('div');
                   fallback.innerHTML = '<div style="width: 260px; height: 260px; background: #fff; border-radius: 2rem; border: 4px solid #000000; display: flex; align-items: center; justify-content: center; box-shadow: 0 8px 40px 0 rgba(255,211,0,0.18), 0 2px 16px 0 rgba(0,0,0,0.18);"><span style="color: #000; font-size: 2rem; font-weight: bold; text-align: center;">GEOMATIXS<br/>GROUP<br/>CORP.</span></div>';
                   e.target.parentElement.appendChild(fallback);
                 }}
               />
           </div>
         </div>
       </section>
       )}

      {/* About Us Section (Tabbed) */}
      {currentPage === 'home' && (
        <section id="about" className="section about-section section-animate anim-fade">
        <div className="container">
          <h2 className="section-title">About Us</h2>
          <nav className="about-tabs" role="tablist">
            <button
              className={`about-tab ${activeTab === 'history' ? 'active' : ''}`}
              onClick={() => setActiveTab('history')}
              role="tab"
              aria-selected={activeTab === 'history'}
            >
              History
            </button>
            <button
              className={`about-tab ${activeTab === 'timeline' ? 'active' : ''}`}
              onClick={() => setActiveTab('timeline')}
              role="tab"
              aria-selected={activeTab === 'timeline'}
            >
              Timeline
            </button>
            <button
              className={`about-tab ${activeTab === 'advantage' ? 'active' : ''}`}
              onClick={() => setActiveTab('advantage')}
              role="tab"
              aria-selected={activeTab === 'advantage'}
            >
              Competitive Advantage
            </button>

          </nav>

          {activeTab === 'history' && (
            <div className="about-content active" role="tabpanel">
              <div className="about-content-layout">
                <div className="about-text">
                  <h3 className="about-subtitle">Brief History</h3>
                  <p className="about-paragraph">
                    Geomatixs was founded in 2020 to provide engineering services related to construction of industrial, commercial, residential buildings, infrastructures, and public works. Since then, the company has substantially increased the size and scope of its service offerings through a series of strategic personnel acquisitions and internal growth.
                  </p>
                  <p className="about-paragraph">
                    Today, GEOMATIXS GROUP CORP. is a leading provider of engineering and technical testing services.
                  </p>
                  <p className="about-paragraph">
                    From the beginning, GEOMATIXS has attracted the best technical personnel in the field of materials testing and has always focused on bringing innovative solutions to our client's most complex needs.
                  </p>
                  <p className="about-paragraph">
                    Today, GEOMATIXS has 60 technical personnel, deployed in 7 laboratories and 1 in-house laboratory located in Cavite, Laguna, and Batangas.
                  </p>
                </div>
                                 <div className="about-image">
                   <img src={`${process.env.PUBLIC_URL}/bgphoto.png?v=${Date.now()}`} alt="Geomatixs Testing Wallpaper" className="about-img" />
                 </div>
              </div>
            </div>
          )}

          {activeTab === 'timeline' && (
            <div className="about-content active" role="tabpanel">
              <h3 className="about-subtitle">Timeline</h3>
              <nav className="timeline-subtabs">
                <button
                  className={`timeline-subtab ${activeTimelineTab === 0 ? 'active' : ''}`}
                  onClick={() => setActiveTimelineTab(0)}
                  role="tab"
                  aria-selected={activeTimelineTab === 0}
                >
                  2020–2021
                </button>
                <button
                  className={`timeline-subtab ${activeTimelineTab === 1 ? 'active' : ''}`}
                  onClick={() => setActiveTimelineTab(1)}
                  role="tab"
                  aria-selected={activeTimelineTab === 1}
                >
                  2022–2023
                </button>
                <button
                  className={`timeline-subtab ${activeTimelineTab === 2 ? 'active' : ''}`}
                  onClick={() => setActiveTimelineTab(2)}
                  role="tab"
                  aria-selected={activeTimelineTab === 2}
                >
                  2024–2025
                </button>
              </nav>
              <div className="timeline-content">
                <div className="timeline-connector"></div>
                <div className="timeline-events">
                  {timelineData[activeTimelineTab].map((event, index) => (
                    <div key={index} className="timeline-event">
                      <div className="timeline-icon">
                        <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                          <circle cx="12" cy="12" r="10" stroke="#FFD300" strokeWidth="2" fill="none"/>
                          <path d="M12 8v4l3 3" stroke="#FFD300" strokeWidth="2"/>
                        </svg>
                      </div>
                      <div className="timeline-date">{event.date}</div>
                      <div className="timeline-description">{event.description}</div>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          )}

          {activeTab === 'advantage' && (
            <div className="about-content active" role="tabpanel">
              <h3 className="about-subtitle">Competitive Advantage</h3>
              <div className="advantage-content">
                <div className="advantage-list">
                  <div className="advantage-item-new">
                    <div className="advantage-icon-new">
                      <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" width="40" height="40" fill="none" stroke="currentColor" strokeWidth="2">
                        <path d="M3 3h18v18H3zM9 9h6v6H9z"/>
                        <path d="M12 3v6M12 15v6M3 12h6M15 12h6"/>
                        <circle cx="12" cy="12" r="2"/>
                      </svg>
                    </div>
                    <div className="advantage-text">
                      <h4>Fast Mobilization</h4>
                      <p>Multiple branches with ready resources and equipment. We can deploy our team on-site within <strong>1-2 hours</strong> after finalizing project requirements and agreements.</p>
                    </div>
                  </div>
                  
                  <div className="advantage-item-new">
                    <div className="advantage-icon-new">
                      <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" width="40" height="40" fill="none" stroke="currentColor" strokeWidth="2">
                        <rect x="2" y="3" width="20" height="14" rx="2" ry="2"/>
                        <line x1="8" y1="21" x2="16" y2="21"/>
                        <line x1="12" y1="17" x2="12" y2="21"/>
                        <path d="M7 7h10M7 11h6"/>
                        <circle cx="16" cy="9" r="1"/>
                      </svg>
                    </div>
                    <div className="advantage-text">
                      <h4>Cost Effective Pricing</h4>
                      <p>Premium service at affordable prices. We employ efficient methods while maintaining quality standards, ensuring the best value for your project investment.</p>
                    </div>
                  </div>
                  
                  <div className="advantage-item-new">
                    <div className="advantage-icon-new">
                      <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" width="40" height="40" fill="none" stroke="currentColor" strokeWidth="2">
                        <path d="M16 21v-2a4 4 0 0 0-4-4H6a4 4 0 0 0-4 4v2"/>
                        <circle cx="9" cy="7" r="4"/>
                        <path d="M22 21v-2a4 4 0 0 0-3-3.87"/>
                        <path d="M16 3.13a4 4 0 0 1 0 7.75"/>
                        <path d="M20 21v-2a4 4 0 0 0-3-3.87"/>
                        <path d="M14 3.13a4 4 0 0 1 0 7.75"/>
                        <path d="M12 2v2M12 20v2M2 12h2M20 12h2"/>
                      </svg>
                    </div>
                    <div className="advantage-text">
                      <h4>Young Professionals</h4>
                      <p>Dedicated team of humble, adaptable professionals who understand complex project requirements and exceed client expectations through commitment and expertise.</p>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          )}


        </div>
      </section>
       )}

      {/* Downloadable Resources Section */}
       {currentPage === 'home' && (
         <section id="resources" className="section resources-section section-animate anim-scale">
        <div className="container">
          <h2 className="section-title">Downloadable Resources</h2>
          <div className="resources-card">
            <div className="resources-content">
              <div className="resources-info">
                <div className="icon-container">
                  <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                  </svg>
                </div>
                <div className="text-content">
                  <h3>Company Profile</h3>
                  <p>Download our comprehensive company profile to learn more about Geomatixs Group Corp., our services, and achievements.</p>
                </div>
              </div>
              <div className="download-button-container">
                <a href="GGC Company Profile 0225.pdf" download className="download-btn btn-animate">
                  <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 10v6m0 0l-3-3m3 3l3-3m2 8H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                  </svg>
                  <span>Download PDF</span>
                </a>
              </div>
            </div>
          </div>
        </div>
      </section>

        )}

       {/* Services Section */}
       {currentPage === 'home' && (
         <section id="services" className="section services-section section-animate anim-up">
        <div className="container">
                     <div className="services-banner">
             <img src={`${process.env.PUBLIC_URL}/bannerservice.png?v=${Date.now()}`} alt="Geomatixs Services" className="services-banner-img" />
            <div className="services-banner-overlay">
            </div>
          </div>
          <h2 className="section-title">Our Services</h2>
          <div className="services-grid">
                         <div className="service-card">
               <img src={`${process.env.PUBLIC_URL}/concrete.jpg`} alt="Concrete Testing" className="service-img" />
              <h3 className="service-title">Concrete</h3>
              <p className="service-description">Advanced concrete testing and analysis for structural integrity and compliance.</p>
                             <button onClick={() => setCurrentPage('services')} className="service-btn btn-animate">Learn More &gt;</button>
            </div>
                         <div className="service-card">
               <img src={`${process.env.PUBLIC_URL}/soil.jpg`} alt="Soil Testing" className="service-img" />
              <h3 className="service-title">Soil | Ground Improvement</h3>
              <p className="service-description">Comprehensive soil testing and ground improvement solutions for safe foundations.</p>
                             <button onClick={() => setCurrentPage('services')} className="service-btn btn-animate">Learn More &gt;</button>
            </div>
                         <div className="service-card">
               <img src={`${process.env.PUBLIC_URL}/steel.jpg`} alt="Steel Testing" className="service-img" />
              <h3 className="service-title">Steel | Pile Testing</h3>
              <p className="service-description">Steel quality assurance and pile testing for robust and reliable structures.</p>
                             <button onClick={() => setCurrentPage('services')} className="service-btn btn-animate">Learn More &gt;</button>
            </div>
                         <div className="service-card">
               <img src={`${process.env.PUBLIC_URL}/nondes.jpg`} alt="NDT Services" className="service-img" />
              <h3 className="service-title">Non-Destructive Testing</h3>
              <p className="service-description">State-of-the-art non-destructive testing for material and structural evaluation.</p>
                             <button onClick={() => setCurrentPage('services')} className="service-btn btn-animate">Learn More &gt;</button>
            </div>
          </div>
        </div>
      </section>
        )}

              {/* Our Valued Clients Section */}
        {currentPage === 'home' && (
          <section id="testimonials" className="section testimonials-section section-animate anim-fade">
         <div className="container">
           <h2 className="section-title text-center">Our Valued Clients</h2>
                       <div className="valued-clients-carousel">
              <div className="carousel-track" style={{ transform: `translateX(${carouselPosition}px)` }}>
                                                  {/* Create infinite loop by duplicating cards multiple times */}
                 {[...clientLogos, ...clientLogos, ...clientLogos, ...clientLogos].map((logo, index) => (
                   <div key={index} className="valued-client-card">
                     <div className="client-card">
                       <div className="client-card-inner">
                         <div className="client-card-content">
                           <div className="client-logo-container">
                                                           <img 
                                src={logo} 
                                alt="Client Logo"
                                className="client-logo-image"
                                onError={(e) => {
                                  console.log('Client logo error:', logo);
                                  e.target.style.display = 'none';
                                  // Add fallback placeholder
                                  const fallback = document.createElement('div');
                                  fallback.innerHTML = '<div style="width: 100%; height: 100%; background: #333; border-radius: 0.5rem; display: flex; align-items: center; justify-content: center; color: #FFD300; font-size: 0.8rem; text-align: center;">Client<br/>Logo</div>';
                                  e.target.parentElement.appendChild(fallback);
                                }}
                              />
                           </div>
                         </div>
                       </div>
                       <div className="client-card-glow"></div>
                     </div>
                   </div>
                 ))}
              </div>
            </div>
         </div>
       </section>
        )}

      {/* Projects Section */}
      {currentPage === 'home' && (
        <section id="projects" className="section projects-section with-bg section-animate anim-up">
        <div className="projects-bg-overlay"></div>
        <div className="container projects-content-wrapper">
                     <div className="projects-banner">
             <img src={`${process.env.PUBLIC_URL}/project.png`} alt="Project Banner" className="projects-banner-img" />
          </div>
          <h2 className="section-title">Projects</h2>
          <div className="projects-cta">
                         <button onClick={() => setCurrentPage('projects')} className="projects-btn btn-animate">View Our Project Gallery</button>
          </div>
        </div>
      </section>
        )}

      {/* Branches Section */}
      {currentPage === 'home' && (
        <section id="branches" className="section branches-section section-animate anim-stagger">
        <div className="container">
          <h2 className="section-title">Our Branches</h2>
          <div className="branches-grid">
            {branches.map((branch, index) => (
              <div
                key={index}
                className={`branch-card ${branch.isMain ? 'branch-card-main' : ''} ${
                  index % 3 === 0 ? 'branch-pos-left' : index % 3 === 1 ? 'branch-pos-middle' : 'branch-pos-right'
                }`}
                style={{ transitionDelay: `${(index % 3) * 80}ms` }}
              >
                <div className="branch-header">
                  <h3 className="branch-name">{branch.name}</h3>
                  {branch.isMain && <span className="main-branch-tag">Main Branch</span>}
                </div>
                <p className="branch-location">{branch.location}</p>
                <p className="branch-contact">{branch.contact}</p>
                <div className="branch-map-card">
                  <div className="google-map-container">
                    <iframe
                      src={branch.isMain ? 
                        "https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d3865.764088896863!2d121.0627906760568!3d14.32513138375973!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x3397d7ad5ca16703%3A0x1d03e76c11a5701a!2sGeomatixs%20Engineering%20and%20Technical%20Testing%20Services!5e0!3m2!1sen!2sph!4v1757038658425!5m2!1sen!2sph" :
                        branch.name === 'Tanza Branch' ?
                        "https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d469.33180540586557!2d120.85344733145797!3d14.388253026077008!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x33962ddb2036a1bd%3A0xcbcaf316062a7682!2sGeomatixs%20Engineering%20and%20Technical%20Testing%20Services!5e0!3m2!1sen!2sph!4v1757039214842!5m2!1sen!2sph" :
                        branch.name === 'Gentri Branch' ?
                        "https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d61846.0212637344!2d120.82809874863277!3d14.347619699999996!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x3397d54e4768f0b7%3A0x82296e101cce07e3!2sGeomatixs%20Engineering%20and%20Technical%20Testing%20Services!5e0!3m2!1sen!2sph!4v1757039283280!5m2!1sen!2sph" :
                        branch.name === 'Batangas Branch' ?
                        "https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d7738.671743729258!2d121.13342310627327!3d14.116334699999992!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x33bd656e361c5abb%3A0x1b4e01a2ae32b15c!2sGeomatixs%20Engineering!5e0!3m2!1sen!2sph!4v1757039402859!5m2!1sen!2sph" :
                        branch.name === 'Silang Branch' ?
                        "https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d30930.687326714527!2d120.92038617431645!3d14.291904800000006!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x33bd792c547da3bd%3A0x13eeb65eb1a7fcaf!2sGeomatixs%20Engineering%20and%20Technical%20Testing%20Services!5e0!3m2!1sen!2sph!4v1757039485713!5m2!1sen!2sph" :
                        branch.name === 'Lima Tech Branch' ?
                        "https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d104209.40566172611!2d121.09084034559653!3d13.916560591483732!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x33bd6de8ff0d7497%3A0x8621cfe6951a9612!2sGeomatixs%20Engineering%20and%20Technical%20Testing%20Services!5e0!3m2!1sen!2sph!4v1757040431437!5m2!1sen!2sph" :
                        branch.name === 'Banlic Branch' ?
                        "https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d574.9028420498578!2d121.15350287171722!3d14.225788045991271!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x33bd6300079b8a93%3A0xd6ec246a018d1aa9!2sGeomatixs%20Engineering%20LGEJV%20Site%20Laboratory!5e0!3m2!1sen!2sph!4v1757039896012!5m2!1sen!2sph" :
                        branch.name === 'Balayan Branch' ?
                        "https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d123910.87294529028!2d120.61699600037257!3d13.945813399999997!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x33bda3369dd6842d%3A0x8502c97675dead52!2sGeomatixs%20Engineering%20and%20Technical%20Testing%20Services!5e0!3m2!1sen!2sph!4v1757040491939!5m2!1sen!2sph" :
                        `https://maps.google.com/maps?q=${encodeURIComponent(branch.location)}&t=&z=15&ie=UTF8&iwloc=&output=embed`
                      }
                      width="100%"
                      height="150"
                      style={{ border: 0, borderRadius: '8px' }}
                      allowFullScreen=""
                      loading="lazy"
                      referrerPolicy="no-referrer-when-downgrade"
                      title={`Map of ${branch.name}`}
                      onError={(e) => {
                        console.log('Google Maps iframe blocked, showing fallback');
                        e.target.style.display = 'none';
                        e.target.parentElement.innerHTML = `
                          <div style="
                            width: 100%; 
                            height: 150px; 
                            background: linear-gradient(135deg, #FFFFFF 0%, #808080 100%); 
                            display: flex; 
                            align-items: center; 
                            justify-content: center; 
                            color: #333; 
                            font-size: 14px;
                            border-radius: 8px;
                          ">
                            Map unavailable - Click "View on Maps" below
                          </div>
                        `;
                      }}
                    ></iframe>
                  </div>
                  <a href={branch.mapLink} target="_blank" rel="noopener noreferrer" className="branch-map-button">View on Maps</a>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>
        )}

      {/* Testing Section */}
      {currentPage === 'home' && (
        <section id="testing" className="section testing-section section-animate">
        <div className="container">
          <div className="testing-preview-container">
            <img 
              src={`${process.env.PUBLIC_URL}/bannerservice.png?v=${Date.now()}`} 
              alt="Geomatixs Testing Services" 
              className="testing-preview-img" 
            />
            <video 
              src={encodeURI(`${process.env.PUBLIC_URL}/videos/COMPRESSIVE TEST.mp4`)} 
              muted 
              loop 
              playsInline 
              preload="metadata" 
              className="testing-video-preview"
               onError={(e) => {
                 console.log('Testing video error:', e);
                 e.target.style.display = 'none';
                 // Show fallback content
                 const fallback = document.createElement('div');
                 fallback.innerHTML = '<p style="color: #FFD300; text-align: center; padding: 2rem;">Video content available</p>';
                 fallback.style.position = 'absolute';
                 fallback.style.top = '50%';
                 fallback.style.left = '50%';
                 fallback.style.transform = 'translate(-50%, -50%)';
                 e.target.parentElement.appendChild(fallback);
               }}
               onMouseEnter={(e) => {
                 try {
                   e.target.play();
                   e.target.style.opacity = '1';
                   if (e.target.previousElementSibling) {
                     e.target.previousElementSibling.style.opacity = '0';
                   }
                 } catch (error) {
                   console.log('Video play error:', error);
                 }
               }}
               onMouseLeave={(e) => {
                 try {
                   e.target.pause();
                   e.target.style.opacity = '0';
                   if (e.target.previousElementSibling) {
                     e.target.previousElementSibling.style.opacity = '1';
                   }
                 } catch (error) {
                   console.log('Video pause error:', error);
                 }
               }}
             />
          </div>
          <div className="testing-content">
            <h2 className="section-title">In-Site Testing</h2>
            <p className="testing-description">
              We offer comprehensive in-site testing services to ensure the quality and safety of your projects, using the latest technology and industry standards.
            </p>
                         <button onClick={() => { setCurrentPage('testing'); setActiveSection('testing'); window.scrollTo(0, 0); }} className="testing-btn btn-animate">View our Testing Videos</button>
          </div>
        </div>
      </section>
        )}

      {/* Call to Action Section */}
      {currentPage === 'home' && (
        <section id="cta" className="section cta-section section-animate">
        <div className="container">
          <h2 className="section-title">Ready to Get Started?</h2>
          <p className="cta-description">
            Contact us today to discuss your project requirements and get a free consultation.
          </p>
          <div className="cta-buttons">
            <a href="#contact" className="cta-btn primary">Contact Us</a>
            <a href="#services" className="cta-btn secondary">Learn More</a>
          </div>
        </div>
      </section>
        )}

      {/* Footer */}
      <footer className="footer">
        <div className="container">
          {/* Certification Logos */}
          <div className="certification-logos">
            <div className="cert-logo">
              <img src={`${process.env.PUBLIC_URL}/FOOTER/ASTM LOGO.png`} alt="ASTM Member" />
            </div>
            <div className="cert-logo">
              <img src={`${process.env.PUBLIC_URL}/geologo-new.png`} alt="Geomatixs Group Corporation" />
            </div>
            <div className="cert-logo">
              <img src={`${process.env.PUBLIC_URL}/FOOTER/DPWH LOGO.png`} alt="DPWH-BRS ACCR" />
            </div>
            <div className="cert-logo">
              <img src={`${process.env.PUBLIC_URL}/FOOTER/Philgeps.png`} alt="PHILGEPS PLATINUM MEMBER" />
            </div>
          </div>

          {/* Footer Content */}
          <div className="footer-content">
            <div className="footer-column">
              <h3 className="footer-heading">
                <svg className="footer-icon" viewBox="0 0 24 24" fill="currentColor">
                  <path d="M12 7V3H2v18h20V7H12zM6 19H4v-2h2v2zm0-4H4v-2h2v2zm0-4H4V9h2v2zm0-4H4V5h2v2zm4 12H8v-2h2v2zm0-4H8v-2h2v2zm0-4H8V9h2v2zm0-4H8V5h2v2zm10 12h-8v-2h2v2zm0-4h-2v-2h2v2zm0-4h-2V9h2v2zm0-4h-2V5h2v2z"/>
                </svg>
                Geomatixs Group
              </h3>
              <div className="footer-links">
                <a href="https://www.facebook.com/geomatixs" target="_blank" rel="noopener noreferrer" className="footer-link">
                  <svg className="footer-icon" viewBox="0 0 24 24" fill="currentColor">
                    <path d="M12 2.04C6.5 2.04 2 6.53 2 12.06C2 17.06 5.66 21.21 10.44 21.96V14.96H7.9V12.06H10.44V9.85C10.44 7.34 11.93 6.11 14.22 6.11C15.31 6.11 16.63 6.29 16.63 6.29V8.62H15.25C13.94 8.62 13.44 9.39 13.44 10.39V12.06H16.42L15.84 14.96H13.44V21.96A10 10 0 0 0 22 12.06C22 6.53 17.5 2.04 12 2.04Z"/>
                  </svg>
                  Facebook
                </a>
                <a href="mailto:geomatixs.information@gmail.com" className="footer-link">
                  <svg className="footer-icon" viewBox="0 0 24 24" fill="currentColor">
                    <path d="M20 4H4c-1.1 0-1.99.9-1.99 2L2 18c0 1.1.9 2 2 2h16c1.1 0 2-.9 2-2V6c0-1.1-.9-2-2-2zm0 4l-8 5-8-5V6l8 5 8-5v2z"/>
                  </svg>
                  Email
                </a>
              </div>
            </div>

            <div className="footer-column">
              <h3 className="footer-heading">Company</h3>
              <div className="footer-links">
                <a href="#about" className="footer-link">About Us</a>
                <a href="#projects" className="footer-link">Projects</a>
                <a href="#branches" className="footer-link">Branches</a>
                <a href="#testing" className="footer-link">Testing</a>
                <a href="mailto:careers@geomatixs.com" className="footer-link">Careers</a>
              </div>
            </div>

            <div className="footer-column">
              <h3 className="footer-heading">Contact</h3>
              <div className="footer-contact">
                <div className="contact-item">
                  <svg className="footer-icon" viewBox="0 0 24 24" fill="currentColor">
                    <path d="M12 2C8.13 2 5 5.13 5 9c0 5.25 7 13 7 13s7-7.75 7-13c0-3.87-3.13-7-7-7zm0 9.5c-1.38 0-2.5-1.12-2.5-2.5s1.12-2.5 2.5-2.5 2.5 1.12 2.5 2.5-1.12 2.5-2.5 2.5z"/>
                  </svg>
                  <span>Unit 24 Paseo de Carmona Bldg. Brgy, Carmona, Cavite</span>
                </div>
                <div className="contact-item">
                  <svg className="footer-icon" viewBox="0 0 24 24" fill="currentColor">
                    <path d="M6.62 10.79c1.44 2.83 3.76 5.14 6.59 6.59l2.2-2.2c.27-.27.67-.36 1.02-.24 1.12.37 2.33.57 3.57.57.55 0 1 .45 1 1V20c0 .55-.45 1-1 1-9.39 0-17-7.61-17-17 0-.55.45-1 1-1h3.5c.55 0 1 .45 1 1 0 1.25.2 2.45.57 3.57.11.35.03.74-.25 1.02l-2.2 2.2z"/>
                  </svg>
                  <span>0977 292 4357</span>
                </div>
                <div className="contact-item">
                  <svg className="footer-icon" viewBox="0 0 24 24" fill="currentColor">
                    <path d="M20 4H4c-1.1 0-1.99.9-1.99 2L2 18c0 1.1.9 2 2 2h16c1.1 0 2-.9 2-2V6c0-1.1-.9-2-2-2zm0 4l-8 5-8-5V6l8 5 8-5v2z"/>
                  </svg>
                  <span>geomatixs.information@gmail.com</span>
                </div>
              </div>
            </div>
          </div>

          {/* Separator Line */}
          <div className="footer-separator"></div>

          {/* Copyright */}
          <div className="footer-bottom">
            <p>&copy; 2020 Geomatixs Group Corp. All rights reserved.</p>
          </div>
        </div>
      </footer>

      {/* Floating Contact Button */}
      <button
        id="contact-fab"
        className="floating-contact-btn"
        onClick={openContactModal}
        aria-label="Contact Us"
      >
        <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 32 32" width="28" height="28">
          <circle cx="16" cy="16" r="16" fill="#FFD300"/>
          <path d="M8 12l8 6 8-6" stroke="#1A1A1A" strokeWidth="2" fill="none"/>
          <rect x="8" y="10" width="16" height="12" rx="2" stroke="#1A1A1A" strokeWidth="2" fill="none"/>
        </svg>
      </button>

      {/* Scroll Progress Indicator */}
      <div id="scroll-progress-container" className="scroll-progress-container">
        <svg className="scroll-progress-svg" viewBox="0 0 40 40">
          <circle
            ref={scrollProgressRef}
            className="scroll-progress-circle"
            cx="20"
            cy="20"
            r="18"
            strokeDasharray="125.6"
            strokeDashoffset="125.6"
          />
        </svg>
        <button
          className="scroll-up-btn"
          onClick={scrollToTop}
          style={{ opacity: showScrollTop ? 1 : 0, pointerEvents: showScrollTop ? 'auto' : 'none' }}
        >
          <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 10l7-7m0 0l7 7m-7-7v18" />
          </svg>
        </button>
      </div>

      {/* Contact Modal */}
      {contactModalOpen && (
        <div id="contact-modal" className="contact-modal open" onClick={handleModalBackdropClick}>
          <div className="contact-modal-content">
            <div className="contact-modal-header">
              <h3>Contact Us</h3>
              <button className="contact-modal-close" onClick={closeContactModal}>
                <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>
            <form className="contact-form" onSubmit={handleContactSubmit}>
              <div className="form-group">
                <label htmlFor="modal-name">Name</label>
                <input 
                  type="text" 
                  id="modal-name" 
                  name="name" 
                  required 
                  value={formData.name} 
                  onChange={handleInputChange}
                  className={`form-input ${formErrors.name ? 'error' : ''} ${formData.name ? 'success' : ''}`}
                />
                {formErrors.name && <p className="form-error">{formErrors.name}</p>}
              </div>
              <div className="form-group">
                <label htmlFor="modal-email">Email</label>
                <input 
                  type="email" 
                  id="modal-email" 
                  name="email" 
                  required 
                  value={formData.email} 
                  onChange={handleInputChange}
                  className={`form-input ${formErrors.email ? 'error' : ''} ${formData.email ? 'success' : ''}`}
                />
                {formErrors.email && <p className="form-error">{formErrors.email}</p>}
              </div>
              <div className="form-group">
                <label htmlFor="mobile">Mobile Number *</label>
                <input
                  type="tel"
                  id="mobile"
                  name="mobile"
                  value={formData.mobile}
                  onChange={handleInputChange}
                  className={`form-input ${formErrors.mobile ? 'error' : ''} ${formData.mobile ? 'success' : ''}`}
                  placeholder="09XXXXXXXXX"
                  required
                />
                {formErrors.mobile && (
                  <div className="notifications-container">
                    <div className="error-alert">
                      <div className="flex">
                        <div className="flex-shrink-0">
                          <svg aria-hidden="true" fill="currentColor" viewBox="0 0 20 20" xmlns="http://www.w3.org/2000/svg" className="error-svg">
                            <path clipRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" fillRule="evenodd"></path>
                          </svg>
                        </div>
                        <div className="error-prompt-container">
                          <p className="error-prompt-heading">Invalid mobile number format</p>
                          <div className="error-prompt-wrap">
                            <ul className="error-prompt-list">
                              <li>Mobile number must start with "09"</li>
                              <li>Mobile number must be exactly 11 digits</li>
                              <li>Only numbers are allowed (no spaces or special characters)</li>
                            </ul>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                )}
              </div>
              <div className="form-group">
                <label htmlFor="modal-message">Message</label>
                <textarea 
                  id="modal-message" 
                  name="message" 
                  rows="4" 
                  required 
                  value={formData.message} 
                  onChange={handleInputChange}
                  className={`form-input form-textarea ${formErrors.message ? 'error' : ''} ${formData.message ? 'success' : ''}`}
                />
                {formErrors.message && <p className="form-error">{formErrors.message}</p>}
              </div>
              <button type="submit" className="submit-btn" disabled={isSubmitting}>
                {isSubmitting ? 'Sending...' : 'Send Message'}
              </button>
              {submitMessage && (
                <div className={`submit-message ${submitMessage.includes('successfully') ? 'success' : 'error'}`}>
                  {submitMessage}
                </div>
              )}
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default Home;
